import React, { useEffect, useMemo } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
    Block,
    FlexDiv,
    Heading3,
    palette,
    SpaceBetweenDiv,
    StatusBadge,
    TextDefault,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwareExposeSubNav from './AwareExposeSubNav';

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 9px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }
    td {
        padding: 10px 12px;
        border-bottom: 1px solid ${palette.graphite.cloud};
        color: ${palette.graphite.heavyMetal};
        vertical-align: middle;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

const Metric = styled.div`
    background: ${palette.graphite.white};
    border: 1px solid ${palette.graphite.silver};
    border-radius: 8px;
    padding: 12px 16px;
    flex: 1;
`;

const MetricLabel = styled.div`
    font-size: 11px;
    color: ${palette.graphite.charcoal};
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const MetricValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${({ $color }) => $color || palette.graphite.onyx};
    line-height: 1.2;
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 14px;
    @media (max-width: 900px) {
        grid-template-columns: 1fr 1fr;
    }
`;

function scoreColor(score) {
    if (score == null) return palette.graphite.charcoal;
    if (score >= 70) return palette.vibrant.red;
    if (score >= 50) return palette.vibrant.orange;
    return palette.evergreen.aspargus;
}

function levelBadge(level) {
    if (level === 'Critical' || level === 'High')
        return (
            <StatusBadge text={level || 'High'} color={palette.vibrant.red} />
        );
    if (level === 'Medium')
        return (
            <StatusBadge text="Medium" color={palette.vibrant.orange} />
        );
    if (level === 'Low')
        return (
            <StatusBadge text="Low" color={palette.evergreen.aspargus} />
        );
    return null;
}

function inviteStatusBadge(status) {
    if (status === 'completed')
        return (
            <StatusBadge
                text="Completed"
                color={palette.evergreen.aspargus}
            />
        );
    if (status === 'started')
        return (
            <StatusBadge
                text="In progress"
                color={palette.vibrant.orange}
            />
        );
    if (status === 'reminded')
        return (
            <StatusBadge
                text="Reminded"
                color={palette.vibrant.orange}
            />
        );
    if (status === 'bounced')
        return (
            <StatusBadge text="Bounced" color={palette.vibrant.red} />
        );
    return (
        <StatusBadge text="Invited" color={palette.graphite.charcoal} />
    );
}

function campaignStatusBadge(status) {
    if (status === 'active')
        return (
            <StatusBadge
                text="Active"
                color={palette.vibrant.orange}
            />
        );
    if (status === 'completed')
        return (
            <StatusBadge
                text="Completed"
                color={palette.evergreen.aspargus}
            />
        );
    if (status === 'cancelled')
        return (
            <StatusBadge
                text="Cancelled"
                color={palette.graphite.charcoal}
            />
        );
    return (
        <StatusBadge text="Draft" color={palette.graphite.charcoal} />
    );
}

function AwareExposeCampaignDetail() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const {
        store,
        awareExposeStore,
        commonStore,
    } = useStores();
    const { currentCompany } = store;
    const {
        campaign,
        loadingCampaign,
        launchingCampaign,
        sendingReminders,
        deletingCampaign,
    } = awareExposeStore;

    useEffect(() => {
        if (currentCompany && currentCompany.company_id && campaignId) {
            awareExposeStore.loadCampaign(
                currentCompany.company_id,
                campaignId
            );
        }
    }, [currentCompany && currentCompany.company_id, campaignId]);

    const stats = (campaign && campaign.stats) || {
        total: 0,
        invited: 0,
        started: 0,
        completed: 0,
        avg_score: null,
        high_risk_count: 0,
    };

    const completionPct = useMemo(() => {
        if (!stats.total) return 0;
        return Math.round((stats.completed / stats.total) * 100);
    }, [stats]);

    const handleLaunch = async () => {
        if (!campaign) return;
        try {
            await awareExposeStore.launchCampaign(
                currentCompany.company_id,
                campaign.id
            );
            commonStore.success('Campaign launched');
        } catch (e) {
            commonStore.error(
                awareExposeStore.error || 'Failed to launch campaign'
            );
        }
    };

    const handleRemind = async () => {
        if (!campaign) return;
        commonStore.showConfirm(
            'Send reminders to employees who have not completed the assessment yet?',
            'Send reminders',
            'Remind employees',
            async () => {
                try {
                    const r = await awareExposeStore.sendReminders(
                        currentCompany.company_id,
                        campaign.id
                    );
                    commonStore.success(
                        r && r.sent != null
                            ? `Reminders queued for ${r.sent} employees`
                            : 'Reminders queued'
                    );
                } catch (e) {
                    commonStore.error(
                        awareExposeStore.error || 'Failed to send reminders'
                    );
                }
            }
        );
    };

    const handleCancel = async () => {
        if (!campaign) return;
        commonStore.showConfirm(
            'Cancel this campaign? Employees will no longer be able to complete new assessments, but existing results are kept.',
            'Cancel campaign',
            'Confirm',
            async () => {
                try {
                    await awareExposeStore.cancelCampaign(
                        currentCompany.company_id,
                        campaign.id
                    );
                    commonStore.success('Campaign cancelled');
                } catch (e) {
                    commonStore.error(
                        awareExposeStore.error || 'Failed to cancel campaign'
                    );
                }
            },
            true
        );
    };

    const handleDelete = async () => {
        if (!campaign) return;
        const isActive = campaign.status === 'active';
        commonStore.showConfirm(
            isActive
                ? 'Permanently delete this campaign and all invite data? This cannot be undone.'
                : 'Permanently delete this campaign? This cannot be undone.',
            'Delete campaign',
            'Delete',
            async () => {
                try {
                    await awareExposeStore.deleteCampaign(
                        currentCompany.company_id,
                        campaign.id
                    );
                    commonStore.success('Campaign deleted');
                    navigate('/aware-expose/campaigns');
                } catch (e) {
                    commonStore.error(
                        awareExposeStore.error || 'Failed to delete campaign'
                    );
                }
            },
            true
        );
    };

    if (!campaign) {
        return (
            <Container className="manager-content">
                <HeaderSection
                    heading={'Campaign'}
                    subHeadings={
                        <SubHeaderComponentsList>
                            <SubHeaderText
                                text={
                                    loadingCampaign
                                        ? 'Loading…'
                                        : 'Campaign not found'
                                }
                            />
                        </SubHeaderComponentsList>
                    }
                />
                <AwareExposeSubNav active="campaigns" />
            </Container>
        );
    }

    return (
        <Container className="manager-content">
            <Helmet>
                <title>{campaign.name} — AwareExpose</title>
            </Helmet>
            <HeaderSection
                heading={campaign.name}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                campaign.launched_at
                                    ? `Launched ${new Date(
                                          campaign.launched_at
                                      ).toLocaleDateString()}`
                                    : `Draft created ${new Date(
                                          campaign.created_at
                                      ).toLocaleDateString()}`
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <FlexDiv gap={8}>
                        {campaign.status === 'draft' && (
                            <ThemedButton
                                text={
                                    launchingCampaign
                                        ? 'Launching…'
                                        : 'Launch campaign'
                                }
                                mode={'primary'}
                                disabled={launchingCampaign}
                                onClick={handleLaunch}
                                $noWrap
                            />
                        )}
                        {campaign.status === 'active' && (
                            <ThemedButton
                                text={
                                    sendingReminders
                                        ? 'Sending…'
                                        : 'Send reminders'
                                }
                                mode={'primary'}
                                disabled={sendingReminders}
                                onClick={handleRemind}
                                $noWrap
                            />
                        )}
                        {campaign.status === 'active' && (
                            <ThemedButton
                                text={
                                    deletingCampaign
                                        ? 'Cancelling…'
                                        : 'Cancel campaign'
                                }
                                mode={'secondary'}
                                disabled={deletingCampaign}
                                onClick={handleCancel}
                                $noWrap
                            />
                        )}
                        <ThemedButton
                            text={
                                deletingCampaign ? 'Deleting…' : 'Delete'
                            }
                            mode={'secondary'}
                            disabled={deletingCampaign}
                            onClick={handleDelete}
                            $noWrap
                        />
                    </FlexDiv>
                }
            />
            <AwareExposeSubNav active="campaigns" />

            <SpaceBetweenDiv style={{ marginBottom: 12 }}>
                <FlexDiv gap={12} alignCenter>
                    {campaignStatusBadge(campaign.status)}
                    <TextTiny lighter>
                        Deadline:{' '}
                        {campaign.deadline_at
                            ? new Date(
                                  campaign.deadline_at
                              ).toLocaleDateString()
                            : 'No deadline'}
                    </TextTiny>
                </FlexDiv>
                <Link to="/aware-expose/campaigns" style={{ fontSize: 13 }}>
                    ← Back to campaigns
                </Link>
            </SpaceBetweenDiv>

            <MetricsRow>
                <Metric>
                    <MetricLabel>Invited</MetricLabel>
                    <MetricValue>{stats.total}</MetricValue>
                </Metric>
                <Metric>
                    <MetricLabel>Completed</MetricLabel>
                    <MetricValue>
                        {stats.completed} ({completionPct}%)
                    </MetricValue>
                </Metric>
                <Metric>
                    <MetricLabel>Avg score</MetricLabel>
                    <MetricValue $color={scoreColor(stats.avg_score)}>
                        {stats.avg_score == null ? '—' : stats.avg_score}
                    </MetricValue>
                </Metric>
                <Metric>
                    <MetricLabel>High risk</MetricLabel>
                    <MetricValue $color={palette.vibrant.red}>
                        {stats.high_risk_count}
                    </MetricValue>
                </Metric>
            </MetricsRow>

            <Block tablePadding column>
                <SpaceBetweenDiv style={{ padding: '12px 16px' }}>
                    <Heading3>Employees</Heading3>
                    <TextTiny lighter>
                        {stats.total} invited · {stats.started} in progress ·{' '}
                        {stats.completed} completed
                    </TextTiny>
                </SpaceBetweenDiv>
                {(!campaign.invites || campaign.invites.length === 0) && (
                    <FlexDiv padding="24" center>
                        <TextDefault lighter>
                            {campaign.status === 'draft'
                                ? 'No invites yet — launch the campaign to send invitations.'
                                : 'No invites yet.'}
                        </TextDefault>
                    </FlexDiv>
                )}
                {campaign.invites && campaign.invites.length > 0 && (
                    <Table>
                        <thead>
                            <tr>
                                <th>Employee</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Score</th>
                                <th>Level</th>
                                <th>Completed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaign.invites.map((inv) => (
                                <tr key={inv.id}>
                                    <td
                                        style={{
                                            fontWeight: 500,
                                            color: palette.graphite.onyx,
                                        }}
                                    >
                                        {inv.name || inv.email}
                                    </td>
                                    <td>{inv.email}</td>
                                    <td>{inviteStatusBadge(inv.status)}</td>
                                    <td>
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                color: scoreColor(
                                                    inv.exposure_score
                                                ),
                                            }}
                                        >
                                            {inv.exposure_score == null
                                                ? '—'
                                                : inv.exposure_score}
                                        </span>
                                    </td>
                                    <td>
                                        {inv.exposure_level
                                            ? levelBadge(
                                                  inv.exposure_level
                                              )
                                            : '—'}
                                    </td>
                                    <td>
                                        {inv.completed_at
                                            ? new Date(
                                                  inv.completed_at
                                              ).toLocaleString()
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwareExposeCampaignDetail);
